/* eslint-disable no-restricted-syntax */
// This file implements the Picture component itself, so <img> tags are allowed here
import { ReactElement, CSSProperties } from "react";

import {
    $AssetModel,
    ViewModelError
} from "@atlassiansox/bxp-content-framework";

import { breakpoints, containerWidths } from "../../styles";

export type PictureProps = {
    readonly image: $AssetModel;
    readonly altText?: string;
    readonly decorative?: boolean;
};

export const Picture = ({
    image,
    altText,
    decorative
}: PictureProps): ReactElement => {
    const { url, title, width, height } = image;

    const altAttributeValue = altText ? altText : title;

    if (!url) return <ViewModelError key={`${Date.now()}`} model={image} />;

    /**
     * TODO: add Sentry error boundary with tags
     * scope.setTags({team: 'bxpt', modelType: 'asset', contentId: id})
     */

    if (url.endsWith(".svg")) {
        const imageStyle: CSSProperties = { maxWidth: "100%", height: "auto" };
        return (
            <img
                style={imageStyle}
                src={url}
                alt={decorative ? "" : altAttributeValue}
                width={width}
                height={height}
                loading="lazy"
            />
        );
    }

    /**
     * If a valid width is found, restrict image width based on container size
     *
     * When large images are uploaded, this guarantees images will be full resolution
     * by assuming the image can be the full width of the container. If that width is
     * not available then use the native image size.
     */
    if (width && Number.isInteger(width) && width > 0) {
        function getWidthForContainer(
            imgWidth: number,
            containerWidth: number,
            pixelDensity = 1
        ) {
            if (imgWidth > containerWidth * pixelDensity) {
                return containerWidth * pixelDensity;
            }
            return imgWidth;
        }

        const xlWidth = getWidthForContainer(width, containerWidths.xl);
        const xlWidthRetina = getWidthForContainer(
            width,
            containerWidths.xl,
            2
        );
        const lgWidth = getWidthForContainer(width, containerWidths.lg);
        const lgWidthRetina = getWidthForContainer(
            width,
            containerWidths.lg,
            2
        );
        const mdWidth = getWidthForContainer(width, containerWidths.md);
        const mdWidthRetina = getWidthForContainer(
            width,
            containerWidths.md,
            2
        );
        const smWidth = getWidthForContainer(width, containerWidths.sm);
        const smWidthRetina = getWidthForContainer(
            width,
            containerWidths.sm,
            2
        );

        return (
            <picture>
                <source
                    type="image/webp"
                    media={`(min-width: ${breakpoints.xl}px)`}
                    srcSet={`${url}?w=${xlWidthRetina}&fm=webp 2x, ${url}?w=${xlWidth}&fm=webp`}
                />
                <source
                    media={`(min-width: ${breakpoints.xl}px)`}
                    srcSet={`${url}?w=${xlWidthRetina} 2x, ${url}?w=${xlWidth}`}
                />
                <source
                    type="image/webp"
                    media={`(min-width: ${breakpoints.lg}px)`}
                    srcSet={`${url}?w=${lgWidthRetina}&fm=webp 2x, ${url}?w=${lgWidth}&fm=webp`}
                />
                <source
                    media={`(min-width: ${breakpoints.lg}px)`}
                    srcSet={`${url}?w=${lgWidthRetina} 2x, ${url}?w=${lgWidth}`}
                />
                <source
                    type="image/webp"
                    media={`(min-width: ${breakpoints.md}px)`}
                    srcSet={`${url}?w=${mdWidthRetina}&fm=webp 2x, ${url}?w=${mdWidth}&fm=webp`}
                />
                <source
                    media={`(min-width: ${breakpoints.md}px)`}
                    srcSet={`${url}?w=${mdWidthRetina} 2x, ${url}?w=${mdWidth}`}
                />
                <source
                    type="image/webp"
                    media={`(min-width: ${breakpoints.sm}px)`}
                    srcSet={`${url}?w=${smWidthRetina}&fm=webp 2x, ${url}?w=${smWidth}&fm=webp`}
                />
                <source
                    media={`(min-width: ${breakpoints.sm}px)`}
                    srcSet={`${url}?w=${smWidthRetina} 2x, ${url}?w=${smWidth}`}
                />
                <img
                    srcSet={`${url}?w=${smWidthRetina} 2x`}
                    src={`${url}?w=${smWidth}`}
                    alt={decorative ? "" : altAttributeValue}
                    width={width / 2}
                    height={height ? height / 2 : undefined}
                    loading="lazy"
                />
            </picture>
        );
    }

    return (
        <picture>
            <source
                type="image/webp"
                media={`(min-width: ${breakpoints.lg}px)`}
                srcSet={`${url}?w=${breakpoints.xl}&fm=webp`}
            />
            <source
                media={`(min-width: ${breakpoints.lg}px)`}
                srcSet={`${url}?w=${breakpoints.xl}`}
            />
            <source
                type="image/webp"
                media={`(min-width: ${breakpoints.md}px)`}
                srcSet={`${url}?w=${breakpoints.lg}&fm=webp`}
            />
            <source
                media={`(min-width: ${breakpoints.md}px)`}
                srcSet={`${url}?w=${breakpoints.lg}`}
            />
            <source
                type="image/webp"
                media={`(min-width: ${breakpoints.sm}px)`}
                srcSet={`${url}?w=${breakpoints.md}&fm=webp`}
            />
            <source
                media={`(min-width: ${breakpoints.sm}px)`}
                srcSet={`${url}?w=${breakpoints.md}`}
            />
            <source
                type="image/webp"
                srcSet={`${url}?w=${breakpoints.sm}&fm=webp`}
            />
            <img
                style={{ maxWidth: "100%", height: "auto" }}
                src={`${url}?w=${breakpoints.sm}`}
                alt={decorative ? "" : altAttributeValue}
                width={width ? width / 2 : undefined}
                height={height ? height / 2 : undefined}
                loading="lazy"
            />
        </picture>
    );
};
